import { useCallback, useEffect, useState } from 'react';
import { currentDrawerId, getCheckPayments, getDailyReports } from 'api';
import type { Report } from 'api/types';

type UseSessionCheckTotalArgs = {
  activation?: string;          // ISO8601 (preferably with Z/offset)
  deactivation?: string | null; // ISO8601 (preferably with Z/offset)
  storeId?: string;
};

type CheckPayment = {
  amount: number;
  processedAt: string;          // ISO8601 timestamp
  externalOrderId?: string;
};

const CHECKS_CACHE = new Map<string, CheckPayment[]>();

function padTwoDigits(n: number) {
  return n < 10 ? `0${n}` : String(n);
}

function parseUtcDate(isoString: string | null | undefined): Date | null {
  if (!isoString) return null;
  return new Date(isoString);
}

function ensureIsoUtc(iso: string) {
  return /(Z|[+-]\d{2}:?\d{2})$/i.test(iso) ? iso : `${iso}Z`;
}

/** Build daily keys using UTC midnights to avoid TZ drift (keeps original signature). */
export function makeDateKeys(startDateIso: string, endDateIso: string, storeId: string) {
  const s = new Date(`${startDateIso}T00:00:00.000Z`);
  const e = new Date(`${endDateIso}T00:00:00.000Z`);
  if (isNaN(s.getTime()) || isNaN(e.getTime()) || s.getTime() > e.getTime()) return [];
  const keys: string[] = [];
  const current = new Date(s.getTime());
  while (current.getTime() <= e.getTime()) {
    const year = current.getUTCFullYear();
    const month = padTwoDigits(current.getUTCMonth() + 1);
    const day = padTwoDigits(current.getUTCDate());
    keys.push(`${storeId}_${year}${month}${day}`);
    current.setUTCDate(current.getUTCDate() + 1);
  }
  return keys;
}

function pickActiveOrLatestReport(reports: Report[]): Report | null {
  if (!reports?.length) return null;
  const active = reports.find((report) => !report.deactivation_timestamp);
  return active ?? reports[0];
}

export function useSessionCheckTotal(
  args?: UseSessionCheckTotalArgs,
  options?: { enabled?: boolean; autoDetect?: boolean }
) {
  const { enabled = true, autoDetect = true } = options ?? {};
  const [isLoading, setIsLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [checkTotal, setCheckTotal] = useState(0);
  const [checkList, setCheckList] = useState<CheckPayment[]>([]);
  const [sessionInfo, setSessionInfo] =
    useState<{ activation: Date; deactivation: Date; storeId: string } | null>(null);

  const resolveSession = useCallback(async () => {
    if (args?.activation) {
      const activation = parseUtcDate(args.activation)!;
      const deactivation = args?.deactivation ? parseUtcDate(args.deactivation)! : new Date();
      const storeId = args?.storeId ?? currentDrawerId();
      return { activation, deactivation, storeId };
    } else {
      if (!autoDetect) throw new Error('Activation timestamp not provided.');
      const { reports } = await getDailyReports();
      const report = pickActiveOrLatestReport(reports);
      if (!report?.activation_timestamp) throw new Error('No activation timestamp found.');
      const activation = parseUtcDate(report.activation_timestamp)!;
      const deactivation = report.deactivation_timestamp
        ? parseUtcDate(report.deactivation_timestamp)!
        : new Date();
      const storeId = currentDrawerId();
      return { activation, deactivation, storeId };
    }
  }, [args?.activation, args?.deactivation, args?.storeId, autoDetect]);

  const fetchChecks = useCallback(async () => {
    setIsLoading(true);
    setErrorMessage(null);
    try {
      const session = await resolveSession();
      setSessionInfo(session);

      // Build date strings using UTC getters (shape unchanged: YYYY-MM-DD)
      const startDate = `${session.activation.getUTCFullYear()}-${padTwoDigits(
        session.activation.getUTCMonth() + 1
      )}-${padTwoDigits(session.activation.getUTCDate())}`;
      const endDate = `${session.deactivation.getUTCFullYear()}-${padTwoDigits(
        session.deactivation.getUTCMonth() + 1
      )}-${padTwoDigits(session.deactivation.getUTCDate())}`;

      const dateKeys = makeDateKeys(startDate, endDate, session.storeId);

      const missingKeys = dateKeys.filter((key) => !CHECKS_CACHE.has(key));
      if (missingKeys.length) {
        const fetchedArrays = await Promise.all(missingKeys.map((key) => getCheckPayments(key)));
        for (let i = 0; i < missingKeys.length; i++) {
          CHECKS_CACHE.set(missingKeys[i], fetchedArrays[i] as CheckPayment[]);
        }
      }

      const allChecks: CheckPayment[] = dateKeys.reduce((acc: CheckPayment[], key) => {
        const arr = CHECKS_CACHE.get(key);
        if (arr && arr.length) acc.push(...arr);
        return acc;
      }, []);

      // Filter by [activation, deactivation) using UTC instants
      const startMs = session.activation.getTime();
      const endMs = session.deactivation.getTime();
      const filteredChecks = allChecks.filter((cp) => {
        const ts = new Date(ensureIsoUtc(cp.processedAt)).getTime();
        return ts >= startMs && ts < endMs;
      });

      const total = filteredChecks.reduce((sum, cp) => sum + (cp.amount || 0), 0);
      setCheckList(filteredChecks);
      setCheckTotal(total);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('useSessionCheckTotal failed:', err);
      setErrorMessage('Failed to load check payments.');
      setCheckList([]);
      setCheckTotal(0);
    } finally {
      setIsLoading(false);
    }
  }, [resolveSession]);

  useEffect(() => {
    if (!enabled) return;
    fetchChecks();
  }, [fetchChecks, enabled]);

  const refreshChecks = useCallback(() => fetchChecks(), [fetchChecks]);

  return { isLoading, errorMessage, checkTotal, checkList, sessionInfo, refreshChecks };
}
